import { useEffect, useState } from "react";
import { Download, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API, api, errMsg, fmtDate } from "@/lib/api";
import { INVOICE_STATUS, InvoiceLinks, money } from "@/components/billing/Invoices";

const Sum = ({ label, value, id, tone = "" }) => (
  <div className="rounded-lg border border-slate-200 p-4" data-testid={id}>
    <p className="text-sm text-slate-500">{label}</p>
    <p className={`text-xl font-semibold mt-1 ${tone}`}>{value}</p>
  </div>
);

export const PaymentsPanel = () => {
  const [d, setD] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const load = () => api.get("/superadmin/payments").then(({ data }) => setD(data)).catch(() => setD({ invoices: [], error: true }));
  useEffect(() => { load(); }, []);
  const refund = async (i) => {
    const v = window.prompt(`Rembourser la facture ${i.number} (${money(i.amount_paid)} payés).\nMontant à rembourser en € (laisser vide = totalité) :`, "");
    if (v === null) return;
    try {
      const { data } = await api.post(`/superadmin/invoices/${i.id}/refund`, { amount_eur: v.trim() ? Number(v.replace(",", ".")) : null });
      toast.success(`${money(data.refunded_eur)} remboursés au client`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const rows = d.invoices.filter((i) => showTest || !i.is_test);
  return (
    <section className="space-y-4" data-testid="sa-payments">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Paiements et factures</h2>
          <p className="text-sm text-slate-500">Les 100 dernières factures Stripe de tous vos clients.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" data-testid="sa-export-month" />
          <Button asChild variant="outline" size="sm"><a href={`${API}/superadmin/export.csv?month=${month}`} data-testid="sa-export-btn"><Download className="h-4 w-4 mr-1.5" />Export comptable</a></Button>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} data-testid="sa-payments-show-test" />Afficher les comptes de test</label>
      </div>
      {!d.error && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Sum label="Encaissé ce mois-ci" value={money(d.paid_this_month)} id="sa-pay-month" tone="text-green-700" />
          <Sum label="Factures à régler" value={`${d.unpaid_count} · ${money(d.unpaid_amount)}`} id="sa-pay-unpaid" tone={d.unpaid_count ? "text-amber-700" : ""} />
          <Sum label="Clients en défaut de paiement" value={d.past_due_clients} id="sa-pay-pastdue" tone={d.past_due_clients ? "text-red-600" : ""} />
        </div>
      )}
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="py-2 pl-4">Client</th><th>N°</th><th>Date</th><th className="text-right">Montant TTC</th><th className="pl-4">Statut</th><th className="pr-4" /></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={6} className="py-6 text-center text-slate-500">{d.error ? "Impossible de lire les factures Stripe." : "Aucune facture."}</td></tr>}
            {rows.map((i) => {
              const [label, cls] = INVOICE_STATUS[i.status] || [i.status, "bg-slate-100"];
              return (
                <tr key={i.id} className="border-t border-slate-100" data-testid={`sa-invoice-${i.id}`}>
                  <td className="py-2.5 pl-4">{i.org_name}{i.is_test && <Badge variant="outline" className="ml-2 text-[10px]">Test</Badge>}</td>
                  <td className="font-mono text-xs">{i.number || "—"}</td>
                  <td>{fmtDate(i.date)}</td>
                  <td className="text-right font-medium">{money(i.total, i.currency)}</td>
                  <td className="pl-4"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>
                    {i.refunded > 0 && <span className="block text-[11px] text-violet-700 mt-0.5" data-testid={`sa-refunded-${i.id}`}>Remboursé {money(i.refunded)}</span>}</td>
                  <td className="pr-4 text-right whitespace-nowrap"><InvoiceLinks i={i} />
                    {i.status === "paid" && i.refunded < i.amount_paid && <Button size="sm" variant="ghost" className="ml-2 h-7 text-slate-500" onClick={() => refund(i)} title="Rembourser" data-testid={`sa-refund-${i.id}`}><Undo2 className="h-3.5 w-3.5" /></Button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
